import { useReducer } from 'react';
import { useTheme } from '@mui/material';
import { Feature, LineString } from 'geojson';

import { Layer, LayerProps, Source, useMapInstance } from 'shared/map-exports';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { ON_TOP_OF_EVERYTHING_LAYER_ID } from 'features/mapping/styles/constants';
import { LatLng } from 'shared/types';

import {
  IconFootprint,
  trimmedEndpointsLatLng,
} from './MarkerEditConnectorLine.utils';

type MarkerEditConnectorLineProps = {
  fromLatLng: LatLng;
  toLatLng: LatLng;
  footprint: IconFootprint;
  id?: string;
};

// This is a bit complicated since it's trying to draw a line between two icons where the positions relative to each
// other requires the line to start/end at any side of the icon, and the icons are of various sizes/shapes.
export const MarkerEditConnectorLine = (
  props: MarkerEditConnectorLineProps,
): JSX.Element | null => {
  const {
    fromLatLng,
    toLatLng,
    footprint,
    id = 'marker-edit-connector',
  } = props;
  const theme = useTheme();
  const map = useMapInstance();

  // This forces re-rendering of the line when the user pans/zooms the map (which is already somewhat debounced).
  const [, bumpVersion] = useReducer((v: number) => v + 1, 0);
  useMapEvent('move', bumpVersion, map, true);

  if (!map) return null;

  const { fromEdgeLatLng, toEdgeLatLng } = trimmedEndpointsLatLng(
    map,
    fromLatLng,
    toLatLng,
    footprint,
  );
  const data: Feature<LineString> = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: [
        [fromEdgeLatLng.lng, fromEdgeLatLng.lat],
        [toEdgeLatLng.lng, toEdgeLatLng.lat],
      ],
    },
  };

  const lineStyle: LayerProps = {
    type: 'line',
    paint: {
      'line-color': theme.palette.error.main,
      'line-opacity': 1,
      'line-width': 2.5,
      'line-dasharray': [2, 1],
    },
  };

  return (
    <Source id={id} type="geojson" data={data}>
      <Layer
        id={`${id}-line`}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...lineStyle}
      />
    </Source>
  );
};
